/**
 * AutomationBuilderModal — Sprint 6: Visual Workflow Builder
 *
 * A block-based (not drag-drop) automation composer. Deliberately linear so it
 * works on mobile, is keyboard-navigable, and matches how our automations
 * actually execute (trigger → actions in sequence).
 *
 * Blocks:
 *   ⚡ TRIGGER  — picked from /registry/triggers (21 types)
 *   ▼ ACTION 1 — picked from /registry/actions (21 types)
 *   ▼ ACTION 2 — add / remove / reorder
 *
 * On save we POST /api/automation-engine with the same schema the templates use,
 * so anything you build here is a first-class citizen of the same engine.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Zap, ArrowDown, Plus, Trash2, ChevronUp, ChevronDown, Save, Play } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/automation-engine`;
const authHeaders = () => {
  const t = localStorage.getItem('konnect_access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const MODULES = [
  { v: 'crm', l: 'CRM' }, { v: 'sales', l: 'Sales' }, { v: 'marketing', l: 'Marketing' },
  { v: 'service', l: 'Service' }, { v: 'operations', l: 'Operations' }, { v: 'hr', l: 'HR' },
  { v: 'finance', l: 'Finance' }, { v: 'analytics', l: 'Analytics' }, { v: 'website', l: 'Website' },
];

// Sensible per-type param scaffolds so users don't stare at a blank textarea.
const ACTION_PARAM_HINTS = {
  'record.create': { entity: 'lead', status: 'new' },
  'record.update': { entity: 'lead', field: 'stage', value: 'qualified' },
  'record.delete': { entity: 'lead' },
  'field.set': { entity: 'lead', field: 'priority', value: 'high' },
  'email.send': { template: 'welcome', to_role: 'lead_owner' },
  'notification.inapp': { role: 'manager', urgency: 'medium' },
  'sms.send': { template: 'appointment_reminder' },
  'task.create': { title: 'Follow up', assigned_role: 'sales', due_in_hours: 24 },
  'status.change': { entity: 'ticket', from: 'open', to: 'in_progress' },
  'sequence.add': { sequence: 'onboarding' },
  'sequence.remove': { sequence: 'reengagement' },
  'score.calculate': { entity: 'lead', adjustment: 10 },
  'document.generate': { template: 'invoice_pdf', format: 'pdf' },
  'approval.request': { role: 'manager', sla_hours: 24 },
  'webhook.call': { url: 'https://example.com/hook', method: 'POST' },
  'log.timeline': { note: 'Custom log entry' },
  'automation.trigger': { automation_id: 'another-id' },
  'ai.summarize': { scope: 'day', modules: ['sales'] },
  'ai.score': { entity: 'lead' },
  'ai.classify': { entity: 'ticket' },
  'ai.suggest': { entity: 'deal' },
};

const TRIGGER_PARAM_HINTS = {
  'record.created': { entity: 'lead' },
  'record.updated': { entity: 'lead' },
  'record.deleted': { entity: 'lead' },
  'field.changed': { entity: 'deal', field: 'status', to: 'won' },
  'date.after': { entity: 'invoice', field: 'due_date', days: 1 },
  'date.before': { entity: 'contract', field: 'renewal_date', days: 90 },
  'date.today': { entity: 'employee', fields: ['birthday'] },
  'schedule.cron': { cron: '0 9 * * *' },
  'inactivity': { entity: 'lead', days: 7 },
  'score.crossed': { entity: 'customer', metric: 'churn_score', threshold: 70 },
  'form.submitted': { form_id: 'contact-form' },
  'meeting.scheduled': {},
  'document.signed': {},
  'ai.scored': { entity: 'lead' },
  'manual': {},
};

function jsonBlockValue(obj) {
  try { return JSON.stringify(obj || {}, null, 2); } catch { return '{}'; }
}
function parseJsonBlock(str) {
  try { return JSON.parse(str || '{}'); } catch { return null; }
}

const emptyAction = () => ({ type: 'log.timeline', params: { note: 'New step' }, _rawParams: '' });

export default function AutomationBuilderModal({ open, onClose, onSaved, initial = null }) {
  const [triggers, setTriggers] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [module, setModule] = useState('crm');
  const [triggerType, setTriggerType] = useState('record.created');
  const [triggerParams, setTriggerParams] = useState(jsonBlockValue(TRIGGER_PARAM_HINTS['record.created']));
  const [steps, setSteps] = useState([emptyAction()]);

  // Registry (triggers + actions)
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [t, a] = await Promise.all([
          axios.get(`${API}/registry/triggers`, { headers: authHeaders() }),
          axios.get(`${API}/registry/actions`, { headers: authHeaders() }),
        ]);
        setTriggers(t.data.triggers || []);
        setActions(a.data.actions || []);
      } catch {
        toast.error('Could not load automation registry');
      }
    })();
  }, [open]);

  // Hydrate for edit / duplicate
  useEffect(() => {
    if (!open) return;
    if (initial) {
      setName(initial.name || '');
      setDescription(initial.description || '');
      setModule(initial.module || 'crm');
      setTriggerType(initial.trigger?.type || 'record.created');
      setTriggerParams(jsonBlockValue(initial.trigger?.params));
      setSteps((initial.actions || []).map(a => ({ type: a.type, params: a.params || {}, _rawParams: jsonBlockValue(a.params) })));
      if (!(initial.actions || []).length) setSteps([emptyAction()]);
    } else {
      setName(''); setDescription(''); setModule('crm');
      setTriggerType('record.created');
      setTriggerParams(jsonBlockValue(TRIGGER_PARAM_HINTS['record.created']));
      setSteps([{ ...emptyAction(), _rawParams: jsonBlockValue(emptyAction().params) }]);
    }
  }, [open, initial]);

  const triggersByCat = useMemo(() => {
    const map = {};
    triggers.forEach(t => { (map[t.category] ||= []).push(t); });
    return map;
  }, [triggers]);

  const actionsByCat = useMemo(() => {
    const map = {};
    actions.forEach(a => { (map[a.category] ||= []).push(a); });
    return map;
  }, [actions]);

  // ── Step handlers
  const updateStepType = (idx, newType) => {
    setSteps(s => s.map((st, i) => i === idx
      ? { ...st, type: newType, params: ACTION_PARAM_HINTS[newType] || {}, _rawParams: jsonBlockValue(ACTION_PARAM_HINTS[newType] || {}) }
      : st));
  };
  const updateStepParams = (idx, raw) => setSteps(s => s.map((st, i) => i === idx ? { ...st, _rawParams: raw } : st));
  const addStep = () => setSteps(s => [...s, { ...emptyAction(), _rawParams: jsonBlockValue(emptyAction().params) }]);
  const removeStep = (idx) => setSteps(s => s.length > 1 ? s.filter((_, i) => i !== idx) : s);
  const moveStep = (idx, dir) => {
    setSteps(s => {
      const target = idx + dir;
      if (target < 0 || target >= s.length) return s;
      const c = [...s]; [c[idx], c[target]] = [c[target], c[idx]]; return c;
    });
  };

  const updateTriggerType = (v) => {
    setTriggerType(v);
    setTriggerParams(jsonBlockValue(TRIGGER_PARAM_HINTS[v] || {}));
  };

  const save = async () => {
    if (!name.trim()) return toast.error('Name is required');
    const tParams = parseJsonBlock(triggerParams);
    if (tParams === null) return toast.error('Trigger params must be valid JSON');
    const parsedActions = [];
    for (const [i, st] of steps.entries()) {
      const p = parseJsonBlock(st._rawParams);
      if (p === null) return toast.error(`Action #${i + 1} params must be valid JSON`);
      parsedActions.push({ type: st.type, params: p });
    }
    setLoading(true);
    try {
      const body = {
        name: name.trim(), description, module,
        trigger: { type: triggerType, params: tParams },
        conditions: [],
        actions: parsedActions,
        active: true,
        tags: ['custom', 'builder'],
      };
      if (initial?.id) {
        await axios.patch(`${API}/${initial.id}`, body, { headers: authHeaders() });
        toast.success('Automation updated');
      } else {
        await axios.post(`${API}`, body, { headers: authHeaders() });
        toast.success('Automation created — active immediately');
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="automation-builder">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-emerald-600" />
            {initial ? 'Edit Automation' : 'Build Automation'}
          </DialogTitle>
          <DialogDescription>
            Compose a workflow: <b>one trigger</b> fires a sequence of <b>actions</b>. Save and it&apos;s live.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Automation name *</Label>
              <Input value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. VIP customer welcome flow" data-testid="builder-name" />
            </div>
            <div>
              <Label>Module</Label>
              <Select value={module} onValueChange={setModule}>
                <SelectTrigger data-testid="builder-module"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODULES.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="What business outcome does this automation deliver?" rows={2} data-testid="builder-description" />
          </div>

          {/* Trigger block */}
          <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-4 space-y-3" data-testid="builder-trigger-block">
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-600 text-white border-0">TRIGGER</Badge>
              <span className="text-xs text-gray-medium">When this happens…</span>
            </div>
            <Select value={triggerType} onValueChange={updateTriggerType}>
              <SelectTrigger data-testid="builder-trigger-type"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {Object.entries(triggersByCat).map(([cat, list]) => (
                  <div key={cat}>
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 px-2 pt-2">{cat}</div>
                    {list.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  </div>
                ))}
              </SelectContent>
            </Select>
            <div>
              <Label className="text-xs">Trigger params (JSON)</Label>
              <Textarea value={triggerParams} onChange={e => setTriggerParams(e.target.value)}
                rows={3} className="font-mono text-xs" data-testid="builder-trigger-params" />
            </div>
          </div>

          {/* Arrow */}
          <div className="flex justify-center"><ArrowDown className="h-5 w-5 text-gray-400" /></div>

          {/* Action steps */}
          {steps.map((st, idx) => (
            <div key={idx} className="rounded-xl border border-gray-border bg-white p-4 space-y-3" data-testid={`builder-action-${idx}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">STEP {idx + 1}</Badge>
                  <span className="text-xs text-gray-medium">then…</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" onClick={() => moveStep(idx, -1)} disabled={idx === 0} title="Move up">
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => moveStep(idx, 1)} disabled={idx === steps.length - 1} title="Move down">
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => removeStep(idx)} disabled={steps.length === 1} title="Remove">
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
              <Select value={st.type} onValueChange={v => updateStepType(idx, v)}>
                <SelectTrigger data-testid={`builder-action-type-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {Object.entries(actionsByCat).map(([cat, list]) => (
                    <div key={cat}>
                      <div className="text-[10px] uppercase tracking-wide text-gray-400 px-2 pt-2">{cat}</div>
                      {list.map(a => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              <div>
                <Label className="text-xs">Action params (JSON)</Label>
                <Textarea value={st._rawParams} onChange={e => updateStepParams(idx, e.target.value)}
                  rows={3} className="font-mono text-xs" data-testid={`builder-action-params-${idx}`} />
              </div>
              {idx < steps.length - 1 && <div className="flex justify-center pt-1"><ArrowDown className="h-4 w-4 text-gray-300" /></div>}
            </div>
          ))}

          <Button variant="outline" onClick={addStep} className="w-full" data-testid="builder-add-step">
            <Plus className="mr-2 h-4 w-4" /> Add step
          </Button>
        </div>

        <DialogFooter className="pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={save} disabled={loading} className="bg-emerald-brand hover:bg-emerald-hover" data-testid="builder-save">
            {initial ? <><Save className="mr-2 h-4 w-4" /> Update</> : <><Play className="mr-2 h-4 w-4" /> Save & Activate</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
